/**
 * The "file on you" window: one modal Win94 dialog with a corrupted
 * personnel-photo portrait, ALL-CAPS descriptors, stat bars, and a
 * rotating footer line. All display strings arrive from the caller.
 */
import { hashString, mulberry32 } from './glyphs';

export interface ProfileData {
  title: string;
  descriptors: string[];
  footer: string;
  stats: { label: string; value01: number }[];
  portraitSeed: string;
}

const MONO = "Consolas, 'Courier New', monospace";
const PORTRAIT_PX = 64;
const PORTRAIT_CELLS = 8;

const FALLBACK_PALETTE: readonly [string, string, string] = ['#061206', '#1E8F3A', '#7CFC9A'];
// phosphor-ish tritones, dark to hot
const PALETTES: readonly (readonly [string, string, string])[] = [
  FALLBACK_PALETTE,
  ['#120C02', '#9C6A0A', '#FFCB4D'],
  ['#04101A', '#1B6E8F', '#7ADCF2'],
  ['#100616', '#6E3A8F', '#D9A6F2'],
];

// 4x4 Bayer matrix for ordered dithering
const BAYER4 = [0, 8, 2, 10, 12, 4, 14, 6, 3, 11, 1, 9, 15, 7, 13, 5];

/**
 * 64x64 dithered abstract "photo", deterministic from the seed: mirrored
 * 8x8 blocks shaped into a bust silhouette, ordered-dither texture, plus a
 * couple of glitch scanlines. Looks like a personnel photo that did not
 * survive the archive.
 */
function portraitCanvas(seed: string): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = PORTRAIT_PX;
  canvas.height = PORTRAIT_PX;
  canvas.style.cssText = `width:${PORTRAIT_PX}px;height:${PORTRAIT_PX}px;image-rendering:pixelated;display:block;`;
  const ctx = canvas.getContext('2d');
  if (!ctx) return canvas;

  const rand = mulberry32(hashString(`portrait:${seed}`));
  const palette = PALETTES[Math.floor(rand() * PALETTES.length)] ?? FALLBACK_PALETTE;

  // mirrored 8x8 brightness field (face-like), bust shaping, corruption
  const field = new Float32Array(PORTRAIT_CELLS * PORTRAIT_CELLS);
  for (let y = 0; y < PORTRAIT_CELLS; y++) {
    for (let x = 0; x < PORTRAIT_CELLS; x++) {
      const i = y * PORTRAIT_CELLS + x;
      field[i] =
        x < PORTRAIT_CELLS / 2
          ? rand()
          : field[y * PORTRAIT_CELLS + (PORTRAIT_CELLS - 1 - x)] ?? 0;
    }
  }
  for (let y = 0; y < PORTRAIT_CELLS; y++) {
    for (let x = 0; x < PORTRAIT_CELLS; x++) {
      const dx = (x - 3.5) / 2.6;
      const dy = (y - 3.9) / 3.4;
      const i = y * PORTRAIT_CELLS + x;
      const v = field[i] ?? 0;
      field[i] = dx * dx + dy * dy < 1 ? 0.45 + v * 0.5 : v * 0.4;
    }
  }
  const flips = 2 + Math.floor(rand() * 4);
  for (let i = 0; i < flips; i++) {
    field[Math.floor(rand() * field.length)] = rand();
  }

  // per-pixel ordered dither across the 3-color ramp
  for (let py = 0; py < PORTRAIT_PX; py++) {
    for (let px = 0; px < PORTRAIT_PX; px++) {
      const v = field[(py >> 3) * PORTRAIT_CELLS + (px >> 3)] ?? 0;
      const t = ((BAYER4[(py & 3) * 4 + (px & 3)] ?? 0) + 0.5) / 16;
      const shade = Math.max(0, Math.min(1, v)) * 2;
      const base = Math.min(1, Math.floor(shade));
      const idx = shade - base > t ? base + 1 : base;
      ctx.fillStyle = palette[Math.min(2, idx)] ?? '#000';
      ctx.fillRect(px, py, 1, 1);
    }
  }

  // glitch scanlines: shifted horizontal bands
  const glitches = 1 + Math.floor(rand() * 2);
  for (let i = 0; i < glitches; i++) {
    const gy = Math.floor(rand() * (PORTRAIT_PX - 4));
    const gh = 1 + Math.floor(rand() * 3);
    const shift = 2 + Math.floor(rand() * 6);
    const band = ctx.getImageData(0, gy, PORTRAIT_PX, gh);
    ctx.putImageData(band, shift, gy);
  }
  return canvas;
}

export function showProfileCard(parent: HTMLElement, data: ProfileData): void {
  const wrap = document.createElement('div');
  wrap.style.cssText = [
    'position:fixed',
    'inset:0',
    'display:grid',
    'place-items:center',
    'z-index:40',
    'pointer-events:auto',
    'background:rgba(0,0,0,0.25)',
  ].join(';');

  const win = document.createElement('div');
  win.className = 'w94-raised';
  win.style.cssText = 'width:min(88vw,340px);padding:2px;box-sizing:border-box;';

  const bar = document.createElement('div');
  bar.textContent = data.title;
  bar.style.cssText = [
    'background:linear-gradient(90deg,var(--w94-title-a),var(--w94-title-b))',
    'color:#fff',
    'font:bold 12px/1 monospace',
    'padding:5px 8px',
    'letter-spacing:1px',
  ].join(';');

  const body = document.createElement('div');
  body.style.cssText = 'padding:12px;display:flex;flex-direction:column;gap:10px;';

  // top row: portrait | descriptor list
  const top = document.createElement('div');
  top.style.cssText = 'display:flex;gap:10px;align-items:flex-start;';
  const frame = document.createElement('div');
  frame.className = 'w94-sunken';
  frame.style.cssText = 'flex:0 0 auto;padding:2px;line-height:0;';
  frame.appendChild(portraitCanvas(data.portraitSeed));
  const list = document.createElement('div');
  list.style.cssText = `flex:1;min-width:0;font:bold 12px/1.7 ${MONO};color:var(--w94-text);`;
  for (const descriptor of data.descriptors.slice(0, 4)) {
    const row = document.createElement('div');
    row.style.cssText = 'overflow:hidden;text-overflow:ellipsis;white-space:nowrap;';
    row.textContent = `■ ${descriptor}`;
    list.appendChild(row);
  }
  top.append(frame, list);

  // stat axes: label + sunken bar with filled segment
  const stats = document.createElement('div');
  stats.style.cssText = 'display:flex;flex-direction:column;gap:7px;';
  for (const stat of data.stats) {
    const group = document.createElement('div');
    const label = document.createElement('div');
    label.style.cssText = `font:bold 10px/1 ${MONO};letter-spacing:1px;color:var(--w94-text);margin-bottom:2px;`;
    label.textContent = stat.label;
    const barOuter = document.createElement('div');
    barOuter.className = 'w94-sunken';
    barOuter.style.cssText = 'height:14px;box-sizing:border-box;';
    const fill = document.createElement('div');
    const pct = Math.max(0, Math.min(1, stat.value01)) * 100;
    fill.style.cssText = `height:100%;width:${pct}%;background:var(--w94-title-b);`;
    barOuter.appendChild(fill);
    group.append(label, barOuter);
    stats.appendChild(group);
  }

  const footer = document.createElement('div');
  footer.style.cssText = `font:italic 11px/1.5 ${MONO};color:var(--w94-text);opacity:.75;`;
  footer.textContent = data.footer;

  body.append(top, stats, footer);

  const buttonRow = document.createElement('div');
  buttonRow.style.cssText = 'display:flex;justify-content:center;padding:0 0 12px;';
  const ok = document.createElement('button');
  ok.type = 'button';
  ok.className = 'w94-btn';
  ok.textContent = 'OK';
  ok.style.cssText = 'min-width:96px;height:44px;pointer-events:auto;';
  ok.addEventListener('click', () => wrap.remove());
  buttonRow.appendChild(ok);

  win.append(bar, body, buttonRow);
  wrap.appendChild(win);
  parent.appendChild(wrap);
}
